import type { ReactNode } from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING, Panel, Callout, MetricCounter, Caption } from "../../coldcompute";
import type { AccentName } from "../../coldcompute";
import {
  HOOK,
  S1SETUP,
  S1TENSION,
  S1BROLL,
  S1ONSCREEN,
  S1PAYOFF,
  SEG2,
  SEG3,
  SEG4,
  ENDCARD,
} from "./script";

// ─── local helpers ───────────────────────────────────────────────────────────

/**
 * Spring-in wrapper; `at`/`until` are scene-local frames. `until` hard-hides.
 * `fill` is REQUIRED when the child is full-screen (e.g. <Center>): the wrapper's
 * transform makes it the containing block for inner AbsoluteFills, so without
 * `position:absolute; inset:0` the wrapper is zero-height and the beat renders
 * centered on the frame's top edge (half cut off).
 */
const Reveal: React.FC<{
  at?: number;
  until?: number;
  dy?: number;
  fill?: boolean;
  children: ReactNode;
  style?: React.CSSProperties;
}> = ({ at = 0, until, dy = 26, fill = false, children, style }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  if (until !== undefined && frame >= until) return null;
  const p = spring({ frame: frame - at, fps, config: SPRING.snappy });
  const out =
    until !== undefined
      ? interpolate(frame, [until - 8, until], [1, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        })
      : 1;
  return (
    <div
      style={{
        opacity: p * out,
        transform: `translateY(${(1 - p) * dy}px)`,
        ...(fill ? { position: "absolute" as const, inset: 0 } : {}),
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/** Centered stack container used by most beats. */
const Center: React.FC<{ children: ReactNode; gap?: number }> = ({ children, gap = 28 }) => (
  <AbsoluteFill
    style={{
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap,
      padding: "0 120px 120px",
    }}
  >
    {children}
  </AbsoluteFill>
);

const Kicker: React.FC<{ children: ReactNode; accent?: AccentName }> = ({
  children,
  accent = "cyan",
}) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 24,
      letterSpacing: "0.24em",
      textTransform: "uppercase",
      color: COLORS[accent],
    }}
  >
    {children}
  </div>
);

const Big: React.FC<{ children: ReactNode; size?: number; color?: string }> = ({
  children,
  size = 92,
  color = COLORS.text,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 1.08,
      textAlign: "center",
      color,
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

const Sub: React.FC<{ children: ReactNode; size?: number }> = ({ children, size = 34 }) => (
  <div
    style={{
      fontFamily: FONTS.body,
      fontSize: size,
      lineHeight: 1.45,
      textAlign: "center",
      color: COLORS.dim,
      maxWidth: 1300,
    }}
  >
    {children}
  </div>
);

/**
 * DROP-IN placeholder — deliberately loud (dashed amber) so unreplaced slots are
 * impossible to miss in review. Swap for a real capture via <Img>/<OffthreadVideo>.
 */
const Placeholder: React.FC<{
  label: string;
  note: string;
  width?: number;
  height?: number;
}> = ({ label, note, width = 1120, height = 520 }) => (
  <div
    style={{
      width,
      height,
      border: `3px dashed ${COLORS.amber}88`,
      borderRadius: 14,
      background: "rgba(255,176,32,0.05)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 18,
    }}
  >
    <div
      style={{
        fontFamily: FONTS.mono,
        fontSize: 26,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: COLORS.amber,
      }}
    >
      ⬚ drop-in · {label}
    </div>
    <div
      style={{
        fontFamily: FONTS.body,
        fontSize: 26,
        color: COLORS.dim,
        maxWidth: width - 160,
        textAlign: "center",
        lineHeight: 1.5,
      }}
    >
      {note}
    </div>
  </div>
);

/** One receipt row in the segment-1 ledger. */
const LedgerRow: React.FC<{
  at: number;
  accent: AccentName;
  title: string;
  detail: string;
}> = ({ at, accent, title, detail }) => (
  <Reveal at={at}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 26,
        width: 1240,
        background: COLORS.panel,
        border: `1px solid ${COLORS.line}`,
        borderLeft: `5px solid ${COLORS[accent]}`,
        borderRadius: 10,
        padding: "22px 30px",
      }}
    >
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 26,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: COLORS[accent],
          width: 420,
        }}
      >
        {title}
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.text, flex: 1 }}>
        {detail}
      </div>
    </div>
  </Reveal>
);

/** Mono terminal panel (real probe output — no CodePanel dependency). */
const Terminal: React.FC<{ title: string; lines: { text: string; color?: string }[] }> = ({
  title,
  lines,
}) => (
  <Panel accent="green" title={title} width={1240}>
    <div style={{ padding: "18px 26px", display: "flex", flexDirection: "column", gap: 8 }}>
      {lines.map((l, i) => (
        <div
          key={i}
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            lineHeight: 1.4,
            color: l.color ?? COLORS.text,
            whiteSpace: "pre",
          }}
        >
          {l.text}
        </div>
      ))}
    </div>
  </Panel>
);

// ─── HOOK (1208f · 40.24s) ───────────────────────────────────────────────────

export const SceneHook: React.FC = () => (
  <AbsoluteFill>
    {/* beat 1 · the claim */}
    <Reveal fill until={250}>
      <Center>
        <Kicker accent="red">the claim</Kicker>
        <Big size={110}>
          “APPS AND WEBSITES:
          <br />
          GONE IN SIX YEARS.”
        </Big>
        <Sub>— Elon Musk. Easy to ignore. He says a lot of things.</Sub>
      </Center>
    </Reveal>
    {/* beat 2 · the smash-cut counter */}
    <Reveal fill at={250} until={430}>
      <Center gap={10}>
        <Kicker accent="green">same week</Kicker>
        <MetricCounter
          value={1.5}
          decimals={1}
          prefix="$"
          suffix="B"
          accent="green"
          startFrame={255}
          durationFrames={40}
          fontSize={280}
        />
        <Sub size={38}>Salesforce revenue from basically that exact future</Sub>
      </Center>
    </Reveal>
    {/* beat 3 · stripe/openrouter */}
    <Reveal fill at={430} until={545}>
      <Center>
        <Kicker accent="violet">also same week</Kicker>
        <Big size={96}>STRIPE BOUGHT OPENROUTER</Big>
      </Center>
    </Reveal>
    {/* beat 4 · my roadmap already said this */}
    <Reveal fill at={545} until={720}>
      <Center gap={20}>
        <Kicker accent="cyan">my roadmap · months earlier</Kicker>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <LedgerRow at={555} accent="cyan" title="1 · embeddable widgets" detail="live in other people's apps" />
          <LedgerRow at={575} accent="green" title="2 · widgets over MCP" detail="interactive cards inside the AI" />
          <LedgerRow at={595} accent="magenta" title="3 · the employee" detail="auto-documenting knowledge layer" />
        </div>
      </Center>
    </Reveal>
    {/* beat 5 · title card */}
    <Reveal fill at={720}>
      <Center gap={20}>
        <Kicker accent="amber">ep 47</Kicker>
        <Big size={150}>APPS ARE DYING.</Big>
        <Big size={78} color={COLORS.green}>
          I HAVE THE RECEIPTS.
        </Big>
        <Sub>why “an app people go to” is about to become a liability — and what I'm doing about it</Sub>
      </Center>
    </Reveal>
    <Caption lines={HOOK} accent="amber" />
  </AbsoluteFill>
);

// ─── S1 SETUP (359f · 11.95s) ────────────────────────────────────────────────

export const SceneS1Setup: React.FC = () => (
  <AbsoluteFill>
    <Center gap={18}>
      <Kicker accent="cyan">segment 1 · the receipts</Kicker>
      <Big size={72}>WHAT ACTUALLY HAPPENED THIS WEEK</Big>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
        <LedgerRow at={70} accent="cyan" title="claudeforce" detail="Salesforce, inside Claude" />
        <LedgerRow at={110} accent="green" title="the numbers" detail="$1.5B · ~$4B · 3.2B AWUs" />
        <LedgerRow at={150} accent="magenta" title="mcp apps" detail="rivals ratify ONE standard" />
        <LedgerRow at={190} accent="violet" title="stripe × openrouter" detail="a payments giant buys the pipe" />
      </div>
    </Center>
    <Caption lines={S1SETUP} accent="cyan" />
  </AbsoluteFill>
);

// ─── S1 TENSION (526f · 17.51s) ──────────────────────────────────────────────

export const SceneS1Tension: React.FC = () => (
  <AbsoluteFill>
    <Center gap={26}>
      <Reveal>
        <Placeholder
          label="claudeforce headline"
          note="Screenshot/B-roll: the Claudeforce announcement headline, then a mock of Salesforce data appearing in a Claude chat window."
          width={1180}
          height={430}
        />
      </Reveal>
      <Reveal at={150}>
        <div style={{ display: "flex", gap: 24 }}>
          <Callout label="claudeforce" accent="cyan" width={560}>
            Salesforce data + 37 governed sales skills — surfaced INSIDE Claude.
          </Callout>
          <Callout label="the tell" accent="amber" width={560}>
            The biggest CRM on earth just decided its own interface is optional.
          </Callout>
        </div>
      </Reveal>
    </Center>
    <Caption lines={S1TENSION} accent="cyan" />
  </AbsoluteFill>
);

// ─── S1 B-ROLL / THE NUMBERS (700f · 23.33s) ─────────────────────────────────

const Stat: React.FC<{
  at: number;
  value: number;
  decimals?: number;
  prefix?: string;
  suffix: string;
  label: string;
  accent: AccentName;
}> = ({ at, value, decimals = 1, prefix = "$", suffix, label, accent }) => (
  <Reveal at={at}>
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <MetricCounter
        value={value}
        decimals={decimals}
        prefix={prefix}
        suffix={suffix}
        accent={accent}
        startFrame={at + 5}
        durationFrames={35}
        fontSize={170}
      />
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 24,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: COLORS.dim,
        }}
      >
        {label}
      </div>
    </div>
  </Reveal>
);

export const SceneS1Broll: React.FC = () => (
  <AbsoluteFill>
    <Center gap={30}>
      <Kicker accent="green">the numbers behind it</Kicker>
      <div style={{ display: "flex", gap: 110, alignItems: "flex-end" }}>
        <Stat at={30} value={1.5} suffix="B" label="agentforce arr" accent="green" />
        <Stat at={210} value={4} decimals={0} prefix="~$" suffix="B" label="ai + data arr" accent="cyan" />
        <Stat at={390} value={3.2} prefix="" suffix="B" label="agentic work units" accent="magenta" />
      </div>
      <Reveal at={470}>
        <Callout label="+97% in a single quarter" accent="magenta" width={760}>
          Work done by agents — not by a person clicking through screens.
        </Callout>
      </Reveal>
    </Center>
    <Caption lines={S1BROLL} accent="green" />
  </AbsoluteFill>
);

// ─── S1 ON-SCREEN · MCP APPS + STRIPE (1016f · 33.87s) ───────────────────────

/** Tiny chat-window mock with a widget card painting inside it. */
const ChatWidgetMock: React.FC<{ at: number }> = ({ at }) => (
  <div
    style={{
      width: 620,
      background: COLORS.panel2,
      border: `1px solid ${COLORS.line}`,
      borderRadius: 14,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        padding: "12px 18px",
        borderBottom: `1px solid ${COLORS.line}`,
        fontFamily: FONTS.mono,
        fontSize: 18,
        color: COLORS.dim,
      }}
    >
      ● ● ● an AI chat
    </div>
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ alignSelf: "flex-end", background: COLORS.panel, border: `1px solid ${COLORS.line}`, borderRadius: 10, padding: "10px 16px", fontSize: 22, fontFamily: FONTS.body, color: COLORS.text }}>
        show me usage stats
      </div>
      <Reveal at={at} dy={14}>
        <div style={{ border: `1px solid ${COLORS.cyan}66`, borderRadius: 10, padding: 16, background: COLORS.panel, boxShadow: `0 0 26px -14px ${COLORS.cyan}` }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 16, letterSpacing: "0.16em", textTransform: "uppercase", color: COLORS.cyan, marginBottom: 10 }}>
            live widget · served by the tool
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            {["queries 347", "sources 19", "chunks 81k"].map((t) => (
              <div key={t} style={{ flex: 1, background: COLORS.panel2, border: `1px solid ${COLORS.line}`, borderRadius: 8, padding: "12px 10px", textAlign: "center", fontFamily: FONTS.mono, fontSize: 19, color: COLORS.text }}>
                {t}
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </div>
);

export const SceneS1OnScreen: React.FC = () => (
  <AbsoluteFill>
    {/* half 1 · MCP Apps */}
    <Reveal fill until={505}>
      <Center gap={26}>
        <Kicker accent="cyan">mcp apps · ratified january 2026</Kicker>
        <Big size={64}>ANTHROPIC + OPENAI. RIVALS. ONE STANDARD.</Big>
        <ChatWidgetMock at={180} />
      </Center>
    </Reveal>
    {/* half 2 · Stripe owns the pipe */}
    <Reveal fill at={505}>
      <Center gap={34}>
        <Kicker accent="violet">quietly · the same news cycle</Kicker>
        <Big size={72}>STRIPE → OPENROUTER</Big>
        <div style={{ display: "flex", alignItems: "center", gap: 26 }}>
          {[
            { t: "AI APPS", c: COLORS.dim },
            { t: "→", c: COLORS.dim },
            { t: "OPENROUTER (the pipe)", c: COLORS.violet },
            { t: "→", c: COLORS.dim },
            { t: "MODELS", c: COLORS.dim },
          ].map((n, i) => (
            <div
              key={i}
              style={{
                fontFamily: FONTS.mono,
                fontSize: 30,
                color: n.c,
                padding: n.t === "→" ? 0 : "18px 28px",
                border: n.t === "→" ? "none" : `1px solid ${n.c === COLORS.violet ? COLORS.violet : COLORS.line}`,
                borderRadius: 10,
                background: n.t === "→" ? "transparent" : COLORS.panel,
              }}
            >
              {n.t}
            </div>
          ))}
        </div>
        <Sub>A payments company now owns a big chunk of the pipe agents make requests over.</Sub>
      </Center>
    </Reveal>
    <Caption lines={S1ONSCREEN} accent="violet" />
  </AbsoluteFill>
);

// ─── S1 PAYOFF (456f · 15.19s) ───────────────────────────────────────────────

export const SceneS1Payoff: React.FC = () => (
  <AbsoluteFill>
    <Center gap={36}>
      <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {(
            [
              ["claudeforce", "cyan"],
              ["mcp apps", "green"],
              ["agent $", "magenta"],
              ["stripe rails", "violet"],
            ] as const
          ).map(([t, a], i) => (
            <Reveal key={t} at={20 + i * 26}>
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 26,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: COLORS[a],
                  border: `1px solid ${COLORS[a]}55`,
                  borderRadius: 10,
                  padding: "14px 26px",
                  background: COLORS.panel,
                }}
              >
                {t} →
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal at={140}>
          <div
            style={{
              width: 560,
              height: 360,
              background: COLORS.panel2,
              border: `2px solid ${COLORS.text}33`,
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 44, color: COLORS.text }}>
              where the user
            </div>
            <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 44, color: COLORS.text }}>
              already is
            </div>
          </div>
        </Reveal>
      </div>
      <Reveal at={240}>
        <Big size={60} color={COLORS.green}>
          THE CAPABILITY SURVIVES. THE FRONT END DOESN'T.
        </Big>
      </Reveal>
    </Center>
    <Caption lines={S1PAYOFF} accent="green" />
  </AbsoluteFill>
);

// ─── SEG 2 · MY BET (2891f · 96.34s) ─────────────────────────────────────────

const VisionCard: React.FC<{
  n: string;
  title: string;
  lines: string[];
  status: string;
  accent: AccentName;
  statusAccent?: AccentName;
}> = ({ n, title, lines, status, accent, statusAccent = "green" }) => (
  <Panel accent={accent} title={`vision point ${n}`} width={1240}>
    <div style={{ padding: "24px 30px", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 52, color: COLORS.text }}>
        {title}
      </div>
      {lines.map((l) => (
        <div key={l} style={{ fontFamily: FONTS.body, fontSize: 28, lineHeight: 1.45, color: COLORS.dim }}>
          {l}
        </div>
      ))}
      <div
        style={{
          alignSelf: "flex-start",
          fontFamily: FONTS.mono,
          fontSize: 22,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: COLORS[statusAccent],
          border: `1px solid ${COLORS[statusAccent]}66`,
          borderRadius: 999,
          padding: "8px 20px",
          marginTop: 6,
        }}
      >
        {status}
      </div>
    </div>
  </Panel>
);

export const SceneSeg2: React.FC = () => (
  <AbsoluteFill>
    {/* 0–27s · setup + the doc */}
    <Reveal fill until={800}>
      <Center gap={24}>
        <Kicker accent="cyan">segment 2 · my bet, made before the receipts</Kicker>
        <Big size={64}>EAKC — A KNOWLEDGE ENGINE THAT CITES ITS SOURCES</Big>
        <Reveal at={600}>
          <Placeholder
            label="vision-reconciliation doc"
            note="Screen capture: docs/visual-companion/2026-08-28-eakc-vision-reconciliation.html scrolling past the three vision points (Card 1)."
            width={1180}
            height={420}
          />
        </Reveal>
      </Center>
    </Reveal>
    {/* 27–42s · point 1 */}
    <Reveal fill at={800} until={1250}>
      <Center>
        <VisionCard
          n="1"
          accent="cyan"
          title="Widgets embedded in OTHER PEOPLE'S apps"
          lines={[
            "A grounded, interactive answer living in your team's portal — not on my domain.",
            "Signed tokens · sandboxed rendering · the whole thing.",
          ]}
          status="shipped"
        />
      </Center>
    </Reveal>
    {/* 42–66s · point 2 + the real probe terminal */}
    <Reveal fill at={1250} until={1970}>
      <Center gap={20}>
        <VisionCard
          n="2"
          accent="green"
          title="Widgets served over MCP"
          lines={["Ask Claude a question — get a live interactive card, not a wall of text."]}
          status="my side: live-verified on prod · july 2"
        />
        <Reveal at={1450}>
          <Terminal
            title="re-probed against prod · 2026-08-30 (real output)"
            lines={[
              { text: "  PASS  get_usage_stats attached _meta.ui.resourceUri (ui://eakc/…)", color: COLORS.green },
              { text: "  PASS  resources/read renders the usage widget HTML", color: COLORS.green },
              { text: "  PASS  query_eakc widget shows the sources table", color: COLORS.green },
              { text: "  PASS  forged uri → METHOD_NOT_FOUND (fail-closed)", color: COLORS.green },
              { text: "ALL PASS — 26 passed, 0 failed", color: COLORS.text },
            ]}
          />
        </Reveal>
      </Center>
    </Reveal>
    {/* 66–84s · point 3 */}
    <Reveal fill at={1970} until={2520}>
      <Center>
        <VisionCard
          n="3"
          accent="magenta"
          statusAccent="amber"
          title="The employee"
          lines={[
            "Consumes inbound MCP + OpenAPI connections. Auto-documents what it touches.",
            "Flags stale docs. Asks clarifying questions the way a new hire would.",
            "Less something you query — more something that reads everything and raises its hand.",
          ]}
          status="the weird one · see segment 4"
        />
      </Center>
    </Reveal>
    {/* 84–96s · payoff */}
    <Reveal fill at={2520}>
      <Center gap={22}>
        <Kicker accent="green">the endgame</Kicker>
        <Big size={84}>THE KNOWLEDGE LAYER OTHER AIs PLUG INTO</Big>
        <Sub>This week was the first time the rest of the industry said it out loud.</Sub>
      </Center>
    </Reveal>
    <Caption lines={SEG2} accent="cyan" />
  </AbsoluteFill>
);

// ─── SEG 3 · MAKING IT NOT LIE (2965f · 98.81s) ──────────────────────────────

/** The nearness-vs-existence board: a query dot ringed by "near" dots + stamp. */
const NearnessBoard: React.FC<{ at: number }> = ({ at }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const stamp = spring({ frame: frame - (at + 70), fps, config: SPRING.bouncy });
  const dots = [
    { x: 210, y: 60, t: "adjacent fact" },
    { x: 420, y: 130, t: "related module" },
    { x: 120, y: 230, t: "similar name" },
    { x: 470, y: 300, t: "nearby doc" },
    { x: 230, y: 380, t: "same folder" },
  ];
  return (
    <div style={{ position: "relative", width: 660, height: 470 }}>
      {/* the asked-about thing — absent */}
      <div
        style={{
          position: "absolute",
          left: 300,
          top: 205,
          width: 90,
          height: 90,
          borderRadius: "50%",
          border: `3px dashed ${COLORS.red}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONTS.mono,
          fontSize: 17,
          color: COLORS.red,
          textAlign: "center",
        }}
      >
        “does X<br />exist?”
      </div>
      {dots.map((d, i) => (
        <Reveal key={i} at={at + i * 10} dy={10} style={{ position: "absolute", left: d.x, top: d.y }}>
          <div
            style={{
              padding: "10px 16px",
              borderRadius: 999,
              background: COLORS.panel,
              border: `1px solid ${COLORS.cyan}66`,
              fontFamily: FONTS.mono,
              fontSize: 18,
              color: COLORS.cyan,
            }}
          >
            {d.t}
          </div>
        </Reveal>
      ))}
      <div
        style={{
          position: "absolute",
          left: 60,
          top: 170,
          transform: `rotate(-9deg) scale(${stamp})`,
          fontFamily: FONTS.display,
          fontWeight: 700,
          fontSize: 64,
          color: COLORS.red,
          border: `5px solid ${COLORS.red}`,
          borderRadius: 12,
          padding: "8px 22px",
          background: "rgba(255,77,77,0.08)",
        }}
      >
        NEARNESS ≠ EXISTENCE
      </div>
    </div>
  );
};

export const SceneSeg3: React.FC = () => (
  <AbsoluteFill>
    {/* 0–20s · setup + the caught fabrication */}
    <Reveal fill until={600}>
      <Center gap={24}>
        <Kicker accent="red">segment 3 · the unsexy part</Kicker>
        <Big size={80}>MAKING IT NOT LIE</Big>
        <Reveal at={240}>
          <Placeholder
            label="prod chat · the bad answer"
            note="Screen capture: the real chat session scrolling to the confident-yes answer about a feature that does not exist. (Mid-CTA sits in this stretch of the VO.)"
            width={1180}
            height={400}
          />
        </Reveal>
      </Center>
    </Reveal>
    {/* 20–50s · the mechanism */}
    <Reveal fill at={600} until={1500}>
      <Center gap={12}>
        <Kicker accent="red">the mechanism every rag product has</Kicker>
        <NearnessBoard at={640} />
        <Sub>The system fails open — the model stitches adjacent facts into a confident yes.</Sub>
      </Center>
    </Reveal>
    {/* 50–62s · the fix */}
    <Reveal fill at={1500} until={1870}>
      <Center gap={22}>
        <Kicker accent="green">the fix</Kicker>
        <Big size={72}>NEGATIVE EVIDENCE</Big>
        <Sub size={32}>No source directly documents it → the answer is “it doesn't exist.” Full stop.</Sub>
        <Reveal at={1650}>
          <Callout label="+ eval cases" accent="green" width={860}>
            A set of golden cases that permanently punish the old behaviour — so it can't quietly come back.
          </Callout>
        </Reveal>
      </Center>
    </Reveal>
    {/* 62–80s · the paper verdicts */}
    <Reveal fill at={1870} until={2400}>
      <Center gap={26}>
        <Kicker accent="cyan">same discipline · the knowledge-graph paper</Kicker>
        <div style={{ display: "flex", gap: 26 }}>
          {(
            [
              ["ADOPT", "5", "green"],
              ["ALREADY HAD", "4", "cyan"],
              ["REJECT", "1", "red"],
            ] as const
          ).map(([t, v, a], i) => (
            <Reveal key={t} at={1930 + i * 30}>
              <div
                style={{
                  width: 340,
                  background: COLORS.panel,
                  border: `1px solid ${COLORS[a]}66`,
                  borderTop: `5px solid ${COLORS[a]}`,
                  borderRadius: 12,
                  padding: "30px 20px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 120, color: COLORS[a] }}>{v}</div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.18em", color: COLORS.dim }}>{t}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <Sub>Rejected — because my system sits at the exact scale the paper itself calls out as the exception.</Sub>
      </Center>
    </Reveal>
    {/* 80–99s · the line + payoff */}
    <Reveal fill at={2400}>
      <Center gap={26}>
        <Big size={92} color={COLORS.cyan}>
          “RECENCY IS NOT APPLICABILITY.”
        </Big>
        <Sub>The newest fact in your knowledge base isn't automatically the one that answers your question.</Sub>
        <Reveal at={2620}>
          <Callout label="the rule now" accent="green" width={820}>
            Every change gets measured before it counts — a bigger deal than any model upgrade.
          </Callout>
        </Reveal>
      </Center>
    </Reveal>
    <Caption lines={SEG3} accent="red" />
  </AbsoluteFill>
);

// ─── SEG 4 · THE MOMENT IT GOT WEIRD (3365f · 112.15s) ───────────────────────

export const SceneSeg4: React.FC = () => (
  <AbsoluteFill>
    {/* 0–30s · setup + the query */}
    <Reveal fill until={900}>
      <Center gap={24}>
        <Kicker accent="magenta">segment 4 · the moment it got weird</Kicker>
        <Big size={66}>I NEEDED MY VISION NUMBERS. SO I ASKED EAKC.</Big>
        <Reveal at={360}>
          <Placeholder
            label="mcp query · cited answer"
            note="Screen recording: query_eakc over MCP answering the vision question — circle the citation to the doc written YESTERDAY. Let it breathe two seconds."
            width={1180}
            height={430}
          />
        </Reveal>
      </Center>
    </Reveal>
    {/* 30–48s · the self-sync */}
    <Reveal fill at={900} until={1440}>
      <Center gap={30}>
        <Kicker accent="cyan">why the day-old doc was citable</Kicker>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          {(
            [
              ["codebase + docs", "cyan"],
              ["→", "cyan"],
              ["08:00 UTC sync", "green"],
              ["→", "green"],
              ["indexed · citable", "magenta"],
            ] as const
          ).map(([t, a], i) => (
            <Reveal key={i} at={960 + i * 24}>
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 30,
                  color: COLORS[a],
                  padding: t === "→" ? 0 : "20px 30px",
                  border: t === "→" ? "none" : `1px solid ${COLORS[a]}55`,
                  borderRadius: 10,
                  background: t === "→" ? "transparent" : COLORS.panel,
                }}
              >
                {t}
              </div>
            </Reveal>
          ))}
        </div>
        <Sub>Nobody uploaded the file. Nobody tagged it. It re-reads its own repo every morning.</Sub>
      </Center>
    </Reveal>
    {/* 48–66s · the digest mission */}
    <Reveal fill at={1440} until={1980}>
      <Center gap={24}>
        <Kicker accent="green">the second piece · a recurring mission</Kicker>
        <Big size={56}>NEW CHUNKS SINCE LAST SYNC → DRAFTS + EMAILS ME A DIGEST</Big>
        <Reveal at={1600}>
          <Placeholder
            label="digest email"
            note="B-roll: the change-digest mission's email in an inbox — scroll the changed-content list. 'Like a standup update.'"
            width={1080}
            height={360}
          />
        </Reveal>
      </Center>
    </Reveal>
    {/* 66–90s · the honest part */}
    <Reveal fill at={1980} until={2820}>
      <Center gap={26}>
        <Kicker accent="amber">the honest part</Kicker>
        <Big size={64}>
          THE FULL “EMPLOYEE” IS <span style={{ color: COLORS.amber }}>NOT BUILT YET.</span>
        </Big>
        <Sub>Writing docs itself · patching stale ones · asking clarifying questions — the remaining 30%.</Sub>
        <Reveal at={2280}>
          <Panel accent="red" title="caught in my own draft" width={1160}>
            <div style={{ padding: "24px 30px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 40, color: COLORS.dim, textDecoration: "line-through", textDecorationColor: COLORS.red, textDecorationThickness: 4 }}>
                “the knowledge base documented itself”
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 28, lineHeight: 1.5, color: COLORS.text }}>
                The exact nearness-vs-existence trap from segment three. Ingesting a document an AI wrote
                is not the same thing as writing it.
              </div>
            </div>
          </Panel>
        </Reveal>
      </Center>
    </Reveal>
    {/* 90–112s · payoff */}
    <Reveal fill at={2820}>
      <Center gap={16}>
        <Kicker accent="green">what exists today</Kicker>
        <MetricCounter value={70} prefix="~" suffix="%" accent="green" startFrame={2850} durationFrames={40} fontSize={250} />
        <Sub size={36}>of the vision already exists as shipped code or ratified designs</Sub>
        <Reveal at={2990}>
          <Big size={64}>THE REST IS A LIST. NOT A DREAM.</Big>
        </Reveal>
      </Center>
    </Reveal>
    <Caption lines={SEG4} accent="magenta" />
  </AbsoluteFill>
);

// ─── END CARD (300f · no VO yet) ─────────────────────────────────────────────

export const SceneEndCard: React.FC = () => (
  <AbsoluteFill>
    <Center gap={26}>
      <Kicker accent="amber">next video</Kicker>
      <Big size={76}>I CONNECT EAKC TO CLAUDE, LIVE.</Big>
      <Sub>The widget test that sat blocked on the host for eight weeks. What renders, what breaks.</Sub>
      <Reveal at={120}>
        <Placeholder
          label="end-screen cta vo"
          note="END-SCREEN VO NOT RECORDED YET (script v4 'END-SCREEN CTA' + fixed outro block). Record, drop into public/vo/47/, extend this block to its length."
          width={1080}
          height={220}
        />
      </Reveal>
    </Center>
    <Caption lines={ENDCARD} accent="amber" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
